import React, { useCallback, useEffect, useMemo, useState, useRef  } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faList,
  faPenToSquare,
  faTrash,
  faPlus
} from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";
import swal from "sweetalert";
import { APILA } from "../../../../../../config/apila";
import { APIMS } from "../../../../../../config/apims";
import ModalAdd from "./modal/add";
import ModalUpdate from "./modal/add";
import "bulma/css/bulma.css";
import "../../../../../../index.css";

const API_LIMIT = 1000;
const text = (value) => (value == null ? "" : String(value));

export default function ListPerilaku() {
  document.title = "List Perilaku";


  const token = localStorage.getItem("token");
  const storageBranch = localStorage.getItem("ysb_branch_id");
  const storageLevel = localStorage.getItem("level");

  const [page] = useState(1);
  const [limit] = useState(1000);
  const [ascending] = useState(1);
  const [descending] = useState(0);
  const [keyword] = useState("");

  const [isFetching, setIsFetching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isFilterDirty, setIsFilterDirty] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadingSchool, setLoadingSchool] = useState(false);

  const firstLoadDone = useRef(false);
  const initSchoolDone = useRef(false);

  const [modalAdd, setModalAdd] = useState(false);
  const [modalUpdate, setModalUpdate] = useState(false);
  const [deletingId, setDeletingId] = useState(null);


  const [rows, setRows] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [search, setSearch] = useState("");

  const [getDataSemesterMap, setGetDataSemesterMap] = useState([]);
  const [getDataAcademicYears, setGetDataAcademicYears] = useState([]);
  const [getDataBranch, setGetDataBranch] = useState([]);
  const [getDataSchool, setGetDataSchool] = useState([]);
  
  const [namaTA, setNamaTA] = useState("");
  const [namaSmstr, setNamaSmstr] = useState("");
 
  const [formFilter, setFormFilter] = useState({
    ysb_semester_id: "",
    ysb_branch_id: "",
    ysb_school_id: "",
  });

  const location = useLocation();
  const [permission, setPermission] = useState({
    create: 0,
    read: 0,
    update: 0,
    delete: 0,
  });

  const navigate = useNavigate();

  useEffect(() => {
    const storageLevel = localStorage.getItem("level");
    const storageItems = JSON.parse(localStorage.getItem("role_permission")) || [];

    if (storageLevel === "developer" || storageLevel === "superadmin") {
      setPermission({
        create: 1,
        read: 1,
        update: 1,
        delete: 1,
      });
    } else {
      const foundPermission = storageItems.find(
        (item) => item.menu?.url === location.pathname
      );

      if (!foundPermission) {
        navigate("/dashboard");
      } else {
        setPermission({
          create: foundPermission.create,
          read: foundPermission.read,
          update: foundPermission.update,
          delete: foundPermission.delete,
        });
      }
    }
  }, [location.pathname, navigate]);

  const fetchParams = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  // Tahun ajaran dan ID-nya wajib berasal dari GET /api/academic-years.
  const yearOptions = useMemo(
    () => [...new Set(getDataAcademicYears.map((item) => item?.name_year).filter(Boolean))],
    [getDataAcademicYears],
  );
  const selectedAcademicYear = useMemo(
    () => getDataAcademicYears.find((item) => item?.name_year === namaTA),
    [getDataAcademicYears, namaTA],
  );
  const semesterOptions = useMemo(
    () =>
      getDataSemesterMap
        .filter((item) => item?.name_year === namaTA)
        .sort((a, b) => Number(a?.semester) - Number(b?.semester)),
    [namaTA, getDataSemesterMap],
  );

  const isMounted = useRef(true);
  const abortControllers = useRef([]);

  useEffect(() => {
     isMounted.current = true;
     return () => {
       isMounted.current = false;
       abortControllers.current.forEach((c) => c.abort());
     };
   }, []);
 
  const schoolRequestRef = useRef(0);

  // SORT sekolah: SD → SMP → SMA
  const sortSchools = (schools) => {
    const order = ["SD", "SMP", "SMA"];
    return [...schools].sort((a, b) => {
      const jenjangA = order.findIndex((j) => (a.school_name || "").toUpperCase().includes(j));
      const jenjangB = order.findIndex((j) => (b.school_name || "").toUpperCase().includes(j));
      return jenjangA - jenjangB;
    });
  };

  const removeBasementBekasi = (schools, branchId) => {
    if (branchId === "BKS") return schools.filter((s) => s.school_code !== "BASEMENT_BEKASI");
    return schools;
  };

  // =========================
  // INIT: Branch + Semester + Tahun Ajaran
  // =========================
  const fetchInit = useCallback(async () => {
    setLoading(true);
    const controller = new AbortController();
    abortControllers.current.push(controller);

    try {
      const academicYearPayload = {
        page: 1,
        limit: API_LIMIT,
        ascending: 1,
        search: "",
        branch: "",
        level: "user",
      };
      const [branchRes, semesterRes, academicYearRes] = await Promise.all([
        APIMS.get(
          `/api/access/branches?page=${page}&limit=${limit}&ascending=${ascending}&search=${keyword}&branch=${storageBranch}&level=${storageLevel}`,
          { ...fetchParams, signal: controller.signal }
        ),
        APIMS.get(
          `/api/access/semesters?page=${page}&limit=${limit}&ascending=${descending}&search=${keyword}`,
          { ...fetchParams, signal: controller.signal }
        ),
        APIMS.request({
          ...fetchParams,
          method: "GET",
          url: "/api/access/academic-years",
          params: academicYearPayload,
          data: academicYearPayload,
          signal: controller.signal,
        }),
      ]);

      if (!isMounted.current) return;
      if (!(branchRes?.data?.data || []).length) setLoading(false);

      // Branch default pertama
      if (branchRes?.status === 200) {
        const branches = branchRes.data.data || [];
        setGetDataBranch(branches);
        if (branches.length > 0) {
          const firstBranch = branches[0].branch_code;
          setFormFilter((prev) => ({ ...prev, ysb_branch_id: firstBranch }));
        }
      }

      // Tahun ajaran aktif mengikuti endpoint /api/academic-years.
      // ID semester tetap berasal dari endpoint /api/access/semesters.
      if (semesterRes?.status === 200 && academicYearRes?.status === 200) {
        const semesters = Array.isArray(semesterRes.data?.data) ? semesterRes.data.data : [];
        const academicYears = Array.isArray(academicYearRes.data?.data)
          ? academicYearRes.data.data
          : [];
        const activeSemester = semesters.find((s) => Number(s.status) === 1);
        const activeYear = academicYears.find((y) => Number(y.status) === 1)
          || academicYears.find((y) =>
            String(y.id) === String(activeSemester?.ysb_academic_year_id) ||
            y.name_year === activeSemester?.name_year
          )
          || academicYears[0];
        const currentYearSemesters = semesters
          .filter((s) => s?.name_year === activeYear?.name_year)
          .sort((a, b) => Number(a?.semester) - Number(b?.semester));
        const selectedSemester = currentYearSemesters.find((s) => Number(s.status) === 1)
          || currentYearSemesters[0];

        setGetDataAcademicYears(academicYears);
        setGetDataSemesterMap(semesters);
        setNamaTA(activeYear?.name_year || "");
        setNamaSmstr(selectedSemester?.semester || "");
        setFormFilter((prev) => ({
          ...prev,
          ysb_semester_id: selectedSemester?.id || "",
        }));
      }
    } catch (err) {
      if (controller.signal.aborted || !isMounted.current) return;
      setLoading(false);
      swal({
        title: "Failed",
        text: err?.response?.data?.message || err.message || "Error",
        icon: "error",
      });
    } 

  }, [ascending, descending, fetchParams, keyword, limit, page, storageBranch, storageLevel]);

  useEffect(() => {
    fetchInit();
  }, [fetchInit]);

  // =========================
  // Fetch sekolah saat branch berubah
  // =========================
  const fetchSchoolsByBranch = useCallback(
    async (branchId) => {
      const requestId = ++schoolRequestRef.current;
      initSchoolDone.current = false;
      setLoadingSchool(true); // ⬅️ school mulai load
      try {
        const res = await APIMS.get(
          `/api/access/schools?page=${page}&limit=${limit}&ascending=${ascending}&branch=${branchId}&level=${storageLevel}`,
          fetchParams
        );
        if (!isMounted.current || requestId !== schoolRequestRef.current) return;
        if (res?.status === 200) {
          let schools = res.data.data || [];

          // ✅ FILTER ULANG UNTUK ROLE DEVELOPER
          if (storageLevel === "developer" || storageLevel === "superadmin") {
            schools = schools.filter(
              (s) => s.ysb_branch_id === branchId
            );
          }

          schools = removeBasementBekasi(schools, branchId);
          schools = sortSchools(schools);

          setGetDataSchool(schools);

          // default sekolah pertama
          if (schools.length > 0) {
            setFormFilter((prev) => ({
              ...prev,
              ysb_school_id: schools[0].school_code,
            }));
          } else {
            setFormFilter((prev) => ({ ...prev, ysb_school_id: ""}));
          }
        }
      } catch (err) {
        if (isMounted.current && requestId === schoolRequestRef.current) {
          setGetDataSchool([]);
          setFormFilter((prev) => ({ ...prev, ysb_school_id: "" }));
        }
      } finally {
          if (isMounted.current && requestId === schoolRequestRef.current) {
          initSchoolDone.current = true;
          setLoadingSchool(false); // ⬅️ school SELESAI
          setLoading(false);       // ⬅️ shimmer BARU MATI DI SINI
          }
      }
    },[ascending, fetchParams, limit, page, storageLevel]
  );

  useEffect(() => {
    if (formFilter.ysb_branch_id) fetchSchoolsByBranch(formFilter.ysb_branch_id);
  }, [formFilter.ysb_branch_id, fetchSchoolsByBranch]);

  // One filter state for the year dropdown, branch, school, and semester tabs.
  const semesterCache = useRef({});
  const tableRequestRef = useRef(0);

  const isFilterComplete =
    !!selectedAcademicYear?.id && !!formFilter.ysb_semester_id &&
    !!formFilter.ysb_branch_id && !!formFilter.ysb_school_id && !loadingSchool;

  // Data tabel diambil berdasarkan filter yang dipilih, per semester.
  // GET ini memakai JSON body sesuai dokumentasi endpoint.
  const GetResponseData = useCallback(async () => {
    if (!isFilterComplete) return;

    const requestId = ++tableRequestRef.current;
    setIsFetching(true);
    setSearch("");

    try {
      const entries = await Promise.all(
        semesterOptions.map(async (semester) => {
          const payload = {
            page: 1,
            limit: API_LIMIT,
            ascending: true,
            search: "",
            level: "user",
            ysb_academic_year_id: selectedAcademicYear.id,
            ysb_semester_id: semester.id,
            ysb_branch_id: formFilter.ysb_branch_id,
            ysb_school_id: formFilter.ysb_school_id,
          };

          const response = await APILA.request({
            ...fetchParams,
            method: "GET",
            url: "/api/config-anomalys/multiple-indikator",
            params: payload,
            data: payload,
          });

          const responseRows = Array.isArray(response?.data?.data)
            ? response.data.data
            : [];

          // Data lama dapat memiliki ysb_academic_year_id = null.
          // Jangan buang data tersebut jika semester, cabang, dan sekolah sesuai.
          // ID semester sudah mengacu ke tahun ajaran yang dipilih.
          const filteredRows = responseRows.filter((item) => {
            const academicYearMatches =
              item?.ysb_academic_year_id == null ||
              String(item.ysb_academic_year_id) === String(payload.ysb_academic_year_id);

            return (
              academicYearMatches &&
              String(item?.ysb_semester_id) === String(payload.ysb_semester_id) &&
              String(item?.ysb_branch_id) === String(payload.ysb_branch_id) &&
              String(item?.ysb_school_id) === String(payload.ysb_school_id)
            );
          });

          return [String(semester.id), filteredRows];
        })
      );

      if (!isMounted.current || requestId !== tableRequestRef.current) return;

      semesterCache.current = Object.fromEntries(entries);
      setRows(semesterCache.current[String(formFilter.ysb_semester_id)] || []);
      setHasSearched(true);
      setIsFilterDirty(false);
    } catch (error) {
      if (!isMounted.current || requestId !== tableRequestRef.current) return;
      setRows([]);
      setHasSearched(false);
      setIsFilterDirty(true);
      swal({
        title: "Gagal",
        text: error?.response?.data?.message || "Gagal mengambil data list perilaku",
        icon: "error",
      });
    } finally {
      if (isMounted.current && requestId === tableRequestRef.current) {
        setIsFetching(false);
      }
    }
  }, [isFilterComplete, formFilter, semesterOptions, fetchParams, selectedAcademicYear]);

  useEffect(() => {
    if (firstLoadDone.current || isFilterDirty || loading ||
        !initSchoolDone.current || !isFilterComplete) return;
    firstLoadDone.current = true;
    GetResponseData();
  }, [loading, isFilterComplete, isFilterDirty, GetResponseData]);

  const invalidateFilter = () => {
    ++tableRequestRef.current;
    semesterCache.current = {};
    setRows([]);
    setSearch("");
    setIsFetching(false);
    setIsFilterDirty(true);
    setHasSearched(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "ysb_branch_id") {
      ++schoolRequestRef.current;
      initSchoolDone.current = false;
      setGetDataSchool([]);
      setLoadingSchool(!!value);
    }
    setFormFilter((prev) => ({
      ...prev, [name]: value,
      ...(name === "ysb_branch_id" ? { ysb_school_id: "" } : {}),
    }));
    invalidateFilter();
  };

  const changeYear = (event) => {
    const year = event.target.value;
    const options = getDataSemesterMap
      .filter((item) => item?.name_year === year)
      .sort((a, b) => Number(a?.semester) - Number(b?.semester));
    const selected = options.find((item) => Number(item?.status) === 1) || options[0];
    setNamaTA(year);
    setNamaSmstr(selected?.semester || "");
    setFormFilter((prev) => ({ ...prev, ysb_semester_id: selected?.id || "" }));
    invalidateFilter();
  };

  // Dipanggil setelah Add / Update / Delete berhasil: refresh data kedua tab
  // tanpa me-reload browser dan tanpa mengubah filter pengguna.
  const refreshAfterSaved = () => GetResponseData();

  const handleDelete = async (item) => {
    // Gunakan ID baris utama dari GET /api/config-anomalys/multiple-indikator.
    // `group_id` pada indikator bukan ID baris untuk endpoint delete.
    const rowId = item?.id;
    if (!rowId) {
      swal({ title: "Gagal", text: "ID list perilaku tidak ditemukan.", icon: "error" });
      return;
    }

    const approved = await swal({
      title: "Hapus List Perilaku?",
      text: `Perilaku “${item?.description_anomaly || "ini"}” akan dihapus. Lanjutkan?`,
      icon: "warning",
      dangerMode: true,
      buttons: { cancel: "Batal", confirm: { text: "Hapus", value: true } },
    });
    if (!approved) return;

    const userId = (localStorage.getItem("id_admin") || "").replace(/[|&;$%@"<>()+,]/g, "");
    if (!userId) {
      swal({ title: "Gagal", text: "ID pengguna tidak ditemukan. Silakan login ulang.", icon: "error" });
      return;
    }

    setDeletingId(String(rowId));
    try {
      await APILA.delete(
        `/api/config-anomalys/destroy-multiple/${encodeURIComponent(rowId)}`,
        { ...fetchParams, data: { user_id: userId } }
      );
      swal({ title: "Berhasil", text: "List perilaku berhasil dihapus.", icon: "success", timer: 1700 });
      await refreshAfterSaved();
    } catch (error) {
      swal({ title: "Gagal", text: error?.response?.data?.message || "Gagal menghapus list perilaku.", icon: "error" });
    } finally {
      setDeletingId(null);
    }
  };

  const handleCari = async () => {
    if (loadingSchool) {
      swal({ title: "Mohon tunggu", text: "Data sekolah sedang dimuat.", icon: "info" });
      return;
    }
    if (!isFilterComplete) {
      swal({ title: "Filter belum lengkap", text: "Pastikan Tahun Ajaran, Semester, Cabang, dan Sekolah sudah dipilih.", icon: "warning" });
      return;
    }
    await GetResponseData();
  };

  const switchSemester = (semester) => {
    if (!semester?.id || String(semester.id) === String(formFilter.ysb_semester_id)) return;
    setNamaSmstr(semester.semester || "");
    setFormFilter((prev) => ({ ...prev, ysb_semester_id: semester.id }));
    setSearch("");
    setRows(semesterCache.current[String(semester.id)] || []);
  };

  // Satu data perilaku ditampilkan satu baris; indikatornya berada dalam
  // satu kolom sebagai daftar (bukan masing-masing indikator menjadi baris).
  const displayedRows = useMemo(() => {
    const searchText = search.trim().toLowerCase();

    return rows.filter((item) => {
      if (!searchText) return true;

      const indicators = Array.isArray(item?.indikator) ? item.indikator : [];
      return [
        item?.description_anomaly,
        ...indicators.map((indicator) => indicator?.name_sub_element),
      ].some((value) => text(value).toLowerCase().includes(searchText));
    });
  }, [rows, search]);

  const complete =
    !!formFilter.ysb_semester_id &&
    !!formFilter.ysb_branch_id &&
    !!formFilter.ysb_school_id &&
    !loadingSchool;
  const context = {
    academicYear: namaTA,
    academicYearId: selectedAcademicYear?.id || "",
    semesterList: semesterOptions,
    branchId: formFilter.ysb_branch_id,
    branchName: getDataBranch.find(
      (item) => item?.branch_code === formFilter.ysb_branch_id,
    )?.branch_name,
    schoolId: formFilter.ysb_school_id,
    schoolName: getDataSchool.find(
      (item) => item?.school_code === formFilter.ysb_school_id,
    )?.school_name,
    classes: rows,
  };

  return (
    <div className="body sims-page">
      {modalAdd && (
        <ModalAdd
          show
          context={context}
          onSaved={refreshAfterSaved}
          onHide={() => setModalAdd(false)}
        />
      )}
      {modalUpdate && selectedData && (
        <ModalUpdate
          show
          editData={selectedData}
          onSaved={refreshAfterSaved}
          onHide={() => {
            setModalUpdate(false);
            setSelectedData(null);
          }}
        />
      )}
      <div className="page-header-card">
        <div className="page-header-top d-flex">
          <div className="page-title-wrap">
            <span className="page-title-icon">
              <FontAwesomeIcon icon={faList} />
            </span>
            <h4 className="page-title-text">List Perilaku</h4>
          </div>
          <div className="ml-auto">
            <button
              type="button"
              onClick={() => setModalAdd(true)}
              className="btn btn-create sims-header-button"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>List Perilaku</span>
            </button>
          </div>
        </div>

        <nav
          aria-label="breadcrumb"
          className="sims-breadcrumb-nav mt-4"
        >
          <ol className="sims-breadcrumb">
            <li className="sims-breadcrumb-item">
              <Link to="/angket-employees">Penilaian Karyawan</Link>
            </li>

            <li className="sims-breadcrumb-separator">/</li>

            <li
              className="sims-breadcrumb-item active"
              aria-current="page"
            >
              List Perilaku
            </li>
          </ol>
        </nav>
      </div>

      <div className="body-content sims-content">
        {loading ? (
          <div className="sims-filter-card sims-filter-card-two-shimmer">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="shimmer sims-filter-skeleton" />
            ))}
          </div>
        ) : (
          <div className="sims-filter-card sims-filter-card-two">
            <div className="sims-filter-field">
              <label className="sims-filter-label">Tahun Ajaran</label>
              <select
                className="sims-filter-select"
                value={namaTA}
                onChange={changeYear}
              >
                <option value="" disabled>
                  Pilih Tahun Ajaran
                </option>
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}{getDataAcademicYears.some((item) => item.name_year === year && Number(item.status) === 1) ? " (Aktif)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="sims-filter-field">
              <label className="sims-filter-label">Cabang</label>
              <select
                className="sims-filter-select"
                name="ysb_branch_id"
                value={formFilter.ysb_branch_id}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Pilih Cabang
                </option>
                {getDataBranch.map((item) => (
                  <option key={item?.branch_code} value={item?.branch_code}>
                    {item?.branch_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="sims-filter-field">
              <label className="sims-filter-label">Sekolah</label>
              <select
                className="sims-filter-select"
                name="ysb_school_id"
                value={formFilter.ysb_school_id}
                onChange={handleChange}
                disabled={loadingSchool || !formFilter.ysb_branch_id}
              >
                <option value="" disabled>
                  {loadingSchool ? "Memuat data sekolah..." : "Pilih Sekolah"}
                </option>
                {getDataSchool.map((item) => (
                  <option key={item?.school_code} value={item?.school_code}>
                    {item?.school_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="sims-filter-action">
                <button
                  type="button"
                  className="sims-filter-button"
                  disabled={!complete || isFetching || (hasSearched && !isFilterDirty)}
                  onClick={handleCari}
                >
                  Tampilkan Data
                </button>
              </div>

          </div>
        )}

        {isFetching && (
          <div className="sims-loading">
            <PulseLoader color="#3f5bea" size={12} />
          </div>
        )}

        {!loading && !isFetching && !hasSearched && (
          <div className="sims-empty-state">
            Silakan klik <strong>Tampilkan Data</strong> untuk menampilkan data sesuai filter.
          </div>
        )}

        {hasSearched && !isFetching && (
          <>
            <div className="sims-search-wrap mb-3">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="sims-search-icon"
              />
              <input
                type="text"
                className="sims-search-input"
                placeholder="Cari perilaku atau indikator"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            <div className="sims-table-tab-card ">
              <div className="sims-table-tab-head">
                <div className="sims-table-tab-title">
                  <h5>List Perilaku - Semester {namaSmstr}</h5>
                </div>
                <div className="sims-table-tab-semester-switch">
                  {semesterOptions.map((semester) => (
                    <button
                      type="button"
                      key={semester?.id}
                      className={`sims-table-tab-semester ${String(formFilter.ysb_semester_id) === String(semester?.id) ? "is-active" : ""}`}
                      onClick={() => switchSemester(semester)}
                      disabled={isFetching}
                    >
                      Semester {semester?.semester}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="sims-table-tab-wrap">
                <table className="sims-table-tab walas-table-column-borders">
                  <thead>
                    <tr>
                      <th className="sims-col-number">No</th>
                      <th>Perilaku</th>
                      <th>Indikator</th>
                      <th className="sims-col-action">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedRows.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="sims-empty-state">
                          <div className="sims-empty-state-content">
                            Data perilaku tidak ditemukan
                          </div>
                        </td>
                      </tr>
                    ) : (
                      displayedRows.map((item, index) => (
                        <tr key={item?.id || index}>
                          <td className="sims-col-number">
                            <span className="sims-number-badge">
                              {index + 1}
                            </span>
                          </td>

                          <td style={{ fontSize: "13px", verticalAlign: "middle" }}>
                            {item?.description_anomaly || "-"}
                          </td>

                          <td style={{ fontSize: "13px" }}>
                            {Array.isArray(item?.indikator) && item.indikator.length > 0 ? (
                              <ol className="perilaku-indikator-list">
                                {item.indikator.map((indikator, indikatorIndex) => (
                                  <li key={indikator?.group_id || indikatorIndex}>
                                    {indikator?.name_sub_element ||
                                      indikator?.name_element ||
                                      indikator?.name_dimensi || "-"}
                                  </li>
                                ))}
                              </ol>
                            ) : (
                              <span className="has-text-grey">Belum ada indikator</span>
                            )}
                          </td>

                          <td className="sims-col-action">
                            <div className="sims-action-wrap">
                              <button
                                type="button"
                                className="sims-action-button sims-action-edit"
                                title="Ubah List Perilaku"
                                disabled={!permission.update || deletingId !== null}
                                onClick={() => {
                                  setSelectedData(item);
                                  setModalUpdate(true);
                                }}
                              >
                                <FontAwesomeIcon icon={faPenToSquare} />
                              </button>
                              <button
                                type="button"
                                className="sims-action-button sims-action-delete"
                                title="Hapus List Perilaku"
                                disabled={!permission.delete || deletingId !== null}
                                onClick={() => handleDelete(item)}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
      <style>{`
        .perilaku-indikator-list {
          margin: 0;
          padding-left: 19px;
          line-height: 1.6;
        }

        .perilaku-indikator-list li + li {
          margin-top: 5px;
        }

        .walas-table-column-borders > thead > tr > th,
        .walas-table-column-borders > tbody > tr > td {
          border-right: 1px solid #d7e2f7 !important;
        }

        .walas-table-column-borders > thead > tr > th:last-child,
        .walas-table-column-borders > tbody > tr > td:last-child {
          border-right: 0 !important;
        }

        .walas-level-cell {
          vertical-align: middle !important;
          background-color: #f8fafffd !important;
          color: #0066ff !important;
          border : 1px solid #deeafc;
        }

        .walas-name-list {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
          min-width: 190px;
        }

        .walas-name-chip {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          max-width: 230px;
          min-height: 30px;
          padding: 6px 12px 6px 7px;
          border: 1px solid #d7e2f7;
          border-radius: 12px;
          background: #f8faff;
          color: #172554;
          font-size: 12px;
          font-weight: 700;
          line-height: 1.2;
          white-space: nowrap;
        }

        .walas-name-initial {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 28px;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: #dce8ff;
          color: #3f5fca;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: -0.2px;
        }

        .walas-name-text {
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .walas-name-empty {
          color: #94a3b8;
        }

        .walas-action-group {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          white-space: nowrap;
        }

        .walas-action-group .config-matpel-table-button-secondary {
          width: auto;
          min-width: 132px;
          padding: 0 14px;
          gap: 7px;
        }

        .walas-action-group .walas-add-button {
          border-color: #3f5bea !important;
          background: #3f5bea !important;
          color: #ffffff !important;
          box-shadow: 0 4px 10px rgba(63, 91, 234, 0.2);
        }

        .walas-action-group .walas-add-button:hover {
          border-color: #324bc8 !important;
          background: #324bc8 !important;
          color: #ffffff !important;
        }

        .walas-delete-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 36px;
          width: 36px;
          height: 36px;
          border: 1px solid #fecaca;
          border-radius: 9px;
          background: #fff1f2;
          color: #dc2626;
          transition: 0.2s ease;
        }

        .walas-delete-button:hover:not(:disabled) {
          border-color: #fca5a5;
          background: #fee2e2;
        }

        .walas-delete-button:disabled {
          cursor: not-allowed;
          opacity: 0.55;
        }

        @media (max-width: 768px) {
          .walas-name-list {
            min-width: 160px;
          }

          .walas-name-chip {
            max-width: 190px;
          }
        }
      `}</style>
    </div>
  );
}
